import { InputField } from '@/components/form/InputField';
import PriceDisplay from '@/components/PriceDisplay';
import { Form } from '@/components/primitives/Form';
import { AccordionBadgeCount } from '@/components/purchase/checkout/AccordionBadgeCount';
import ToggleableRow from '@/components/purchase/checkout/ToggleableRow';
import { toastSuccess } from '@/components/toast';
import { getProfileActiveVouchers } from '@/features/profile/profileActions';
import {
  activeVouchersProfileSelector,
  profileDataSelector,
} from '@/features/profile/profileSelectors';
import { refreshCheckoutBooking } from '@/features/purchase/purchaseActions';
import { payableAmountSelector } from '@/features/purchase/purchaseSelectors';
import {
  addVoucherToBooking,
  removeVoucherFromBooking,
  validateVoucher,
} from '@/features/purchase/purchaseService';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useCheckoutBooking } from '@/hooks/useCheckoutBooking';
import useConfiguredCurrency from '@/hooks/useConfiguredCurrency';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import type {
  MarkVoucherForPaymentPayloadDTO,
  PendingVoucherDTO,
} from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { format } from '@/utils/dateTime';
import { showErrorToast } from '@/utils/errorToast';
import { createVouchersSchema, type VouchersValues } from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  Button,
  Divider,
  Icons,
  Radio,
  RadioGroup,
  Typography,
} from '@packages/webshop-design-system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';

// Radio value marking the free-text "Add new voucher code" row. Account
// voucher rows use the voucher CODE as their radio value — the backend
// voucher `id` is optional in the DTO (DEV omits it), so it cannot key the
// selection (BR-51922).
const CUSTOM_VOUCHER_CODE_KEY = '__custom__';

function Vouchers() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { currency } = useConfiguredCurrency();
  const booking = useCheckoutBooking();
  const profile = useSelector(profileDataSelector);
  const activeVouchers = useSelector(activeVouchersProfileSelector);
  const { hasSelection } = useChangeBookingSelection();
  const payableAmount = useSelector(payableAmountSelector);
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });

  const form = useForm<VouchersValues>({
    resolver: zodResolver(createVouchersSchema(t)),
    defaultValues: {
      voucherCode: '',
      _meta: {
        customCode: '',
        selectedVoucherCode: null,
      },
    },
  });

  // Fetch active vouchers once per checkout visit when the user is logged
  // in. Deliberately NOT gated on the cached value: balances change when a
  // voucher is spent on a purchase, so the cached list may be stale — it
  // stays on screen while the refresh is in flight (BR-51922).
  const hasFetchedVouchersRef = useRef(false);
  useEffect(() => {
    if (profile && !hasFetchedVouchersRef.current) {
      hasFetchedVouchersRef.current = true;
      dispatch(getProfileActiveVouchers());
    }
  }, [profile, dispatch]);

  // Watch form values
  const selectedVoucherCode = form.watch('_meta.selectedVoucherCode');
  const customCode = form.watch('_meta.customCode');

  // Update voucherCode when customCode changes and custom row is selected
  useEffect(() => {
    if (selectedVoucherCode === CUSTOM_VOUCHER_CODE_KEY && customCode) {
      form.setValue('voucherCode', customCode);
    }
  }, [customCode, selectedVoucherCode, form]);

  // Handle selecting a pre-defined voucher
  const handleVoucherSelect = useCallback(
    (voucherCode: string) => {
      form.setValue('voucherCode', voucherCode);
      form.setValue('_meta.selectedVoucherCode', voucherCode);
      form.clearErrors('voucherCode');
    },
    [form]
  );

  // Handle selecting the custom code row
  const handleCustomCodeSelect = useCallback(() => {
    form.setValue('_meta.selectedVoucherCode', CUSTOM_VOUCHER_CODE_KEY);
    const currentCustomCode = form.getValues('_meta.customCode');
    form.setValue('voucherCode', currentCustomCode || '');
    form.clearErrors('voucherCode');
  }, [form]);

  // Render the content for the custom voucher code row
  function renderCustomCodeContent() {
    if (selectedVoucherCode === CUSTOM_VOUCHER_CODE_KEY) {
      return (
        <InputField
          name="_meta.customCode"
          placeholder={`${t('text.exempliGratia')} 2025ACDF10`}
          hideLabel
          label=""
          classNames={{ wrapper: 'mb-0 flex-1' }}
        />
      );
    }

    if (customCode) {
      return (
        <div className="flex items-center gap-1">
          <Typography variant="body2-bold">{customCode}</Typography>
          <Typography variant="body2" className="text-neutral">
            (<TransText i18nKey="custom" />)
          </Typography>
        </div>
      );
    }

    return (
      <Typography variant="body2">
        <TransText i18nKey="addNewVoucherCode" />
      </Typography>
    );
  }

  // In change-booking we can't safely apply a voucher until the BE payable
  // amount is known — capping at a stale/absent value risks marking the
  // wrong amount for payment. Block voucher entry while it's null.
  const isVoucherEntryBlocked = hasSelection && payableAmount === null;

  const onSubmit = useCallback(
    async (values: VouchersValues) => {
      if (!booking?.id) return;
      if (isVoucherEntryBlocked) return;
      setLoading(true);
      const { voucherCode } = values;

      try {
        const { voucherRemainingValue } = await validateVoucher(
          voucherCode,
          booking.id
        );

        // Cap the voucher's applied amount at what's still owed. In
        // change-booking that's the BE payable amount (the change delta to
        // pay); in a regular purchase it's the booking's provisional price.
        const amountToPay = hasSelection
          ? (payableAmount ?? 0)
          : booking?.provisionalPrice?.amount || 0;
        const paidAmount: MarkVoucherForPaymentPayloadDTO['paidAmount'] = {
          amount: Math.min(voucherRemainingValue, amountToPay),
          currency,
        };

        await addVoucherToBooking(voucherCode, booking.id, paidAmount);
        await dispatch(refreshCheckoutBooking()).unwrap();
        toastSuccess(<TransAlert i18nKey="voucherApplied" />);
        form.reset();
      } catch (e) {
        showErrorToast(
          <TransText i18nKey="voucherCodeNotValid" />,
          e as ApiErrorResponse
        );
      } finally {
        setLoading(false);
      }
    },
    [
      isVoucherEntryBlocked,
      booking?.id,
      booking?.provisionalPrice?.amount,
      hasSelection,
      payableAmount,
      currency,
      dispatch,
      form,
    ]
  );

  const handleRemoveVoucher = useCallback(
    async (voucherCode: string) => {
      if (!booking?.id) return;
      setLoading(true);

      await removeVoucherFromBooking(voucherCode, booking.id);
      setLoading(false);

      await dispatch(refreshCheckoutBooking()).unwrap();
    },
    [booking?.id, dispatch]
  );

  const renderAppliedVoucher = useCallback(
    (voucher: PendingVoucherDTO, index: number) => (
      <div
        className="align-center flex justify-between rounded-md border border-success-border bg-success-surface py-2 pl-2 pr-3 tablet:mr-24 laptop:mr-0"
        key={index}
      >
        <div className="flex shrink-0 items-center gap-2">
          <Icons.checkCircle className="h-4 w-4 text-success-text" />
          <Typography variant="body2-bold">
            <PriceDisplay
              amount={voucher.amount.amount / 10 ** (voucher.amount.scale || 0)}
            />
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          <Typography className="text-neutral" variant="body2">
            {voucher.voucher_code}
          </Typography>
          <Button
            variant="icon"
            data-testid="remove-applied-voucher-button"
            onClick={() => handleRemoveVoucher(voucher.voucher_code)}
            className="h-6 w-6 text-error-text"
          >
            <Icons.x className="h-6 w-6" />
          </Button>
        </div>
      </div>
    ),
    [handleRemoveVoucher]
  );

  return (
    <div className="flex h-full w-full flex-col gap-2 laptop:flex-row laptop:gap-6">
      <div className="flex-1 pt-2">
        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-2"
        >
          <Accordion
            data-testid="collapse-voucher-section-button"
            isExpanded={isOpen}
            onExpandedChange={setIsOpen}
            className="border-none"
            classNames={{
              trigger:
                'justify-between gap-1 text-dark py-0 hover:no-underline',
            }}
            trigger={
              <Typography
                variant="body1-bold"
                className="relative mb-2 text-dark"
              >
                <TransText i18nKey="addVoucher" />:
                {!!booking?.pendingVouchers?.length && (
                  <AccordionBadgeCount
                    count={booking.pendingVouchers.length}
                    label={<TransText i18nKey="appliedVouchers" />}
                  />
                )}
              </Typography>
            }
          >
            <div className="flex w-full flex-col items-start gap-6 self-stretch laptop:flex-row">
              <div className="flex w-full flex-1 flex-col gap-1.5 tablet:flex-row laptop:flex-col laptop:gap-6">
                {profile && !!activeVouchers?.items?.length && (
                  <RadioGroup
                    value={selectedVoucherCode ?? ''}
                    isReadOnly
                    className="mb-2"
                  >
                    {activeVouchers.items
                      .filter((voucher) => voucher.code != null)
                      .map((voucher) => (
                        <ToggleableRow
                          key={voucher.code!}
                          isActive={selectedVoucherCode === voucher.code}
                          onClick={() => handleVoucherSelect(voucher.code!)}
                          data-testid={`voucher-row-${voucher.code}`}
                          className="flex-row"
                        >
                          <Radio value={voucher.code!} size="md" />
                          <div className="flex flex-1 items-center justify-between">
                            <div className="flex flex-col">
                              <Typography variant="body2-bold">
                                {voucher.code}
                              </Typography>
                              {voucher.validUntil && (
                                <Typography
                                  variant="body2"
                                  className="text-neutral"
                                >
                                  {format(
                                    voucher.validUntil,
                                    'EEE, dd MMM yyyy'
                                  )}
                                </Typography>
                              )}
                            </div>
                            <div className="flex flex-row items-center gap-2">
                              <Typography
                                variant="body2"
                                className="text-neutral"
                              >
                                <TransText i18nKey="remaining" />:
                              </Typography>
                              <Typography variant="body2-bold">
                                <PriceDisplay amount={voucher.balance} />
                              </Typography>
                            </div>
                          </div>
                        </ToggleableRow>
                      ))}

                    {/* Custom code row */}
                    <ToggleableRow
                      isActive={selectedVoucherCode === CUSTOM_VOUCHER_CODE_KEY}
                      onClick={handleCustomCodeSelect}
                      data-testid="custom-voucher-row"
                      className="flex-row"
                    >
                      <Radio value={CUSTOM_VOUCHER_CODE_KEY} size="md" />
                      <div className="flex flex-1">
                        {renderCustomCodeContent()}
                      </div>
                    </ToggleableRow>
                  </RadioGroup>
                )}

                {/* Fallback input field - shown when not logged in or no vouchers */}
                {(!profile || !activeVouchers?.items?.length) && (
                  <InputField
                    name="voucherCode"
                    placeholder={`${t('text.exempliGratia')} 2025ACDF10`}
                    required
                    hideLabel
                    label=""
                    classNames={{ wrapper: 'flex-1' }}
                  />
                )}
                <Button
                  variant="secondary"
                  className="flex w-full gap-1.5 tablet:max-w-24 laptop:w-fit"
                  data-testid="add-voucher-button"
                  isPending={loading}
                  isDisabled={isVoucherEntryBlocked}
                  type="submit"
                  startAdornment={
                    <Icons.check className="h-5 w-5 laptop:h-4 laptop:w-4" />
                  }
                >
                  <TransText i18nKey="apply" />
                </Button>
              </div>
              <Divider
                orientation={isLaptopOrBigger ? 'vertical' : 'horizontal'}
                grow={false}
                className="my-2 h-auto self-stretch laptop:my-0"
              />
              <div className="flex w-full flex-1 flex-col gap-2 py-2">
                <Typography variant="body1-bold" className="pb-1">
                  <TransText i18nKey="appliedVouchers" />:
                </Typography>
                <div className="flex w-full flex-col gap-2 overflow-hidden">
                  {booking?.pendingVouchers?.length ? (
                    booking.pendingVouchers.map(renderAppliedVoucher)
                  ) : (
                    <Typography variant="body2" className="text-neutral">
                      <TransText i18nKey="noVouchersApplied" />
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </Accordion>
        </Form>
      </div>
    </div>
  );
}

export default Vouchers;
